import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { AssetStyleId, defaultStyleFor, stylesOfAssetType } from 'fym-shared/src/music24/assetStyles';
import type { KitShape, StudioMoment, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { themeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../../common/types';
import { ThemeStill } from '../../../themes/ThemeStill';
import type { ThemeStillLayout } from '../../../themes/themeStillVars';
import { assetStyleMessages } from '../../../themes/assetStyleMessages';
import { releaseThemeMessages } from '../../../themes/releaseThemeMessages';
import { useIsNarrowScreen } from '../../../work/useIsNarrowScreen';
import { studioMessages } from '../../studioMessages';
import { StoryPreview } from '../../frames/StoryPreview';
import { studioStoryStyle } from '../../frames/studioStory';
import { DetailField } from '../DetailField';
import { PickTile } from './PickTile';
import { isStoryStackType, storyOfMoment, type StoryPreviewContext } from './storyPreviewContext';
import { imageTileMessages } from './imageTileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the image tiles; each part uses its own slice of it */
import styles from './ImageTiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

const IMAGE_LAYOUTS: Partial<Record<AssetStyleId, ThemeStillLayout>> = {
  coverCard: 'card',
  coverGround: 'ground',
  typeOnly: 'type',
};

const BENEFITS: Partial<Record<AssetStyleId, MessageDescriptor>> = {
  coverCard: imageTileMessages.coverCardBenefit,
  coverGround: imageTileMessages.coverGroundBenefit,
  typeOnly: imageTileMessages.typeOnlyBenefit,
};

/** The asset types whose files are 9:16: their previews are tall. */
const TALL_TYPES: ReadonlySet<StudioThemeId> = new Set(['story', 'countdown']);

export interface ImageStyleSong {
  artworkUrl: string | undefined;
  title: string;
  artist: string;
  kicker: string;
}

interface ImageStyleTilesProps {
  assetTypeId: StudioThemeId;
  context: StoryPreviewContext;
  song: ImageStyleSong;
  value: AssetStyleId;
  /** The moment and the line of the step: a Story tile shows the file of that moment. */
  moment?: StudioMoment;
  lyricLine?: string;
  /** The shape of a feed post: its tiles show the 4:5 or the square post. */
  shape?: KitShape;
  onChange: (style: AssetStyleId) => void;
}

/**
 * "Style" of step 3 for an image (design studio-details a1): each style is a large tile with a live
 * preview of the file in the song's theme, a benefit line, the "{theme} default" badge on the
 * default of the theme and the violet ring on the pick. A Story and a feed post tile draw the real
 * Story layout (`StoryPreview`) of the picked moment; the other image types keep the theme still or
 * the lyric card sample.
 */
const ImageStyleTilesComponent: React.FC<ImageStyleTilesProps> = ({
  assetTypeId,
  context,
  song,
  value,
  moment,
  lyricLine,
  shape,
  onChange,
}) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const { look, palette } = context;
  const offered = useMemo(() => stylesOfAssetType(assetTypeId).filter((style) => style !== 'aiFootage'), [assetTypeId]);
  const themeDefault = defaultStyleFor(look.themeId, assetTypeId)?.style ?? null;
  const themeName = intl.formatMessage(releaseThemeMessages[look.themeId].name);
  const story = useMemo(
    () => (isStoryStackType(assetTypeId) ? storyOfMoment(context, moment, lyricLine, assetTypeId, shape) : null),
    [assetTypeId, context, lyricLine, moment, shape]
  );
  const accent = useMemo(() => themeColours(look.themeId, palette).accent, [look.themeId, palette]);

  if (offered.length === 0) {
    return null;
  }

  const previewOf = (styleId: AssetStyleId): React.ReactNode => {
    if (story !== null) {
      return (
        <StoryPreview
          story={story}
          look={look}
          palette={palette}
          storyStyle={studioStoryStyle(styleId)}
          artworkUrl={context.artworkUrl}
          logoUrl={context.logoUrl}
          watermark={context.watermark}
          width={narrow ? 88 : 150}
        />
      );
    }
    return (
      <ThemeStill
        themeId={look.themeId}
        fontOverride={look.fontOverride}
        palette={palette}
        artworkUrl={song.artworkUrl}
        title={song.title}
        artist={song.artist}
        kicker={song.kicker}
        layout={IMAGE_LAYOUTS[styleId] ?? 'card'}
        tall={TALL_TYPES.has(assetTypeId)}
      />
    );
  };

  const benefitOf = (styleId: AssetStyleId): string => {
    const benefit = BENEFITS[styleId];
    return intl.formatMessage(benefit ?? assetStyleMessages[styleId].line);
  };

  return (
    <DetailField
      title={intl.formatMessage(studioMessages.styleTitle)}
      hint={intl.formatMessage(imageTileMessages.styleHint, { theme: themeName })}
    >
      <div
        className={clsx(styles.tiles, offered.length === 2 && styles.two)}
        role="radiogroup"
        aria-label={intl.formatMessage(studioMessages.styleTitle)}
      >
        {offered.map((styleId) => (
          <PickTile
            key={styleId}
            testId={`style-${styleId}`}
            name={intl.formatMessage(assetStyleMessages[styleId].name)}
            isOn={styleId === value}
            preview={previewOf(styleId)}
            badge={
              themeDefault === styleId ? (
                <span className={styles.badge}>
                  <i style={{ background: accent }} />
                  {narrow
                    ? intl.formatMessage(imageTileMessages.defaultShort)
                    : intl.formatMessage(imageTileMessages.themeDefault, { theme: themeName })}
                </span>
              ) : undefined
            }
            benefit={benefitOf(styleId)}
            onPick={() => onChange(styleId)}
          />
        ))}
      </div>
    </DetailField>
  );
};

export const ImageStyleTiles = TypedMemo(ImageStyleTilesComponent);
